import { BaseRepository } from '../BaseRepository';
import { BFF_ENDPOINTS, type BffEndpoint } from '../../endpoints/BffEndpoints';
import type { LoyaltyProgram, PrefillRegistrationResponse, SubscriberRegistrationResponse, SubscriberResponse } from '../../models/bff/Subscriber';
import { BffAuthenticatedApiClient } from '../../clients/BffAuthenticatedApiClient';
import type { HttpResponse } from '../../http/HttpClient';
import type { RegistrationCampaignData, RegistrationPayload } from '../../models/bff/Registration';
import type { BillingContactInput, PersonInput, RegistrationPaymentInput, SubscriptionInput, UpdateUserInput } from '../../models/Registration';

const NAME = 'subscriber';

/** Body fragment carrying `campaigns` only when at least one applies (omitted otherwise). */
function campaignsBody(campaigns?: RegistrationCampaignData[]): { campaigns?: RegistrationCampaignData[] } {
  return campaigns?.length ? { campaigns } : {};
}

/**
 * SubscriberRepository class for handling subscriber-related API operations.
 */
export class SubscriberRepository extends BaseRepository<BffEndpoint> {
  static override name = NAME;

  constructor() {
    super(BffAuthenticatedApiClient.getInstance());
  }

  /**
   * Get the logged-in user's subscriber details
   *
   * @returns Promise<HttpResponse<SubscriberResponse>> - The subscriber of the logged in user
   */
  getUserInfo(): Promise<HttpResponse<SubscriberResponse>> {
    return this.apiClient.get<SubscriberResponse>(BFF_ENDPOINTS.USER_INFO);
  }

  /**
   * Submit a registration request.
   * This will trigger the whole registration flow in the BFF,
   * including setting up the subscriber, plan and processing payment.
   *
   * @param userId - User identity from authentication token
   * @param person - Primary user personal information
   * @param payment - Payment information including token
   * @param subscription - Subscription details (plan, addons, start date, price, loyalty programs)
   * @param billingContact - Billing contact details
   * @returns Promise with workflow ID and status
   */
  submitRegistration(
    userId: string,
    person: PersonInput,
    payment: RegistrationPaymentInput,
    subscription: SubscriptionInput,
    billingContact: BillingContactInput,
    campaigns?: RegistrationCampaignData[],
  ): Promise<HttpResponse<SubscriberRegistrationResponse>> {
    return this.apiClient.post<SubscriberRegistrationResponse, RegistrationPayload>(
      BFF_ENDPOINTS.USER_REGISTRATION,
      {
        identity: { id: userId },
        person: {
          first_name: person.firstName,
          middle_name: person.middleName,
          last_name: person.lastName,
          email: person.email,
          salutation: person.salutation,
          date_of_birth: person.dateOfBirth ? this.formatDateForApi(person.dateOfBirth) : null,
          currency: person.currency,
          locale: person.locale,
          nationality: person.nationality,
          gender: person.gender,
          phones: person.phones,
          addresses: person.addresses?.map(addr => ({
            type: addr.type,
            street: addr.street,
            apartment: addr.apartment,
            city: addr.city,
            state: addr.state,
            postal_code: addr.postalCode,
            country: addr.country,
          })),
          external_id: person.externalId,
          travel_docs: person.travelDocs?.map(doc => ({
            type: doc.type,
            number: doc.number,
            country: doc.country,
            expiration_date: this.formatDateForApi(doc.expirationDate),
          })),
          contacts: person.contacts?.map(contact => ({
            type: contact.type,
            relationship: contact.relationship,
            person: {
              first_name: contact.person.firstName,
              middle_name: contact.person.middleName,
              last_name: contact.person.lastName,
              email: contact.person.email,
              salutation: contact.person.salutation,
              date_of_birth: contact.person.dateOfBirth ? this.formatDateForApi(contact.person.dateOfBirth) : null,
              locale: contact.person.locale,
              nationality: contact.person.nationality,
              gender: contact.person.gender,
              external_id: contact.person.externalId,
              travel_docs: contact.person.travelDocs?.map(doc => ({
                type: doc.type,
                number: doc.number,
                country: doc.country,
                expiration_date: this.formatDateForApi(doc.expirationDate),
              })),
            },
          })) ?? [],
        },
        payment: {
          id: payment.id,
          provider: payment.provider,
          recurrent_token: payment.recurrentToken,
          metadata: payment.metadata,
        },
        subscription: {
          plan: { code: subscription.planCode, addons: subscription.addons },
          start_date: this.formatDateTimeForApi(subscription.startDate),
          price: subscription.price,
        },
        subscriber: { loyalty_programs: subscription.loyaltyPrograms },
        billing_contact: billingContact ? {
          first_name: billingContact.firstName,
          middle_name: billingContact.middleName,
          last_name: billingContact.lastName,
          email: billingContact.email,
          phones: billingContact.phones,
          addresses: billingContact.addresses?.map(addr => ({
            type: addr.type,
            street: addr.street,
            apartment: addr.apartment,
            city: addr.city,
            state: addr.state,
            postal_code: addr.postalCode,
            country: addr.country,
          })),
          external_id: billingContact.externalId,
          travel_docs: billingContact.travelDocs?.map(doc => ({
            type: doc.type,
            number: doc.number,
            country: doc.country,
            expiration_date: this.formatDateForApi(doc.expirationDate),
          })),
        } : undefined,
        ...campaignsBody(campaigns),
      },
    );
  }

  getPrefillRegistrationData(): Promise<HttpResponse<PrefillRegistrationResponse>> {
    return this.apiClient.get<PrefillRegistrationResponse>(BFF_ENDPOINTS.PREFILL_REGISTRATION_DATA);
  }

  /**
   * Update the logged-in user's information.
   *
   * @param data - User information to update
   * @returns Promise<HttpResponse<SubscriberResponse>> - The updated subscriber
   */
  updateUserInfo(data: UpdateUserInput, loyaltyPrograms: LoyaltyProgram[]): Promise<HttpResponse<SubscriberResponse>> {
    return this.apiClient.patch(
      BFF_ENDPOINTS.USER_INFO,
      {
        person: {
          first_name: data.firstName,
          middle_name: data.middleName,
          last_name: data.lastName,
          email: data.email,
          salutation: data.salutation,
          date_of_birth: data.dateOfBirth ? this.formatDateForApi(data.dateOfBirth) : undefined,
          currency: data.currency,
          locale: data.locale,
          nationality: data.nationality,
          gender: data.gender,
          phones: data.phones,
          addresses: data.addresses?.map(addr => ({
            type: addr.type,
            street: addr.street,
            apartment: addr.apartment,
            city: addr.city,
            state: addr.state,
            postal_code: addr.postalCode,
            country: addr.country,
          })),
          external_id: data.externalId,
          travel_docs: data.travelDocs?.map(doc => ({
            type: doc.type,
            number: doc.number,
            country: doc.country,
            expiration_date: this.formatDateForApi(doc.expirationDate),
          })),
        },
        loyalty_programs: loyaltyPrograms ?? [],
      },
    );
  }
}

declare module '../Repositories' {
  export interface Repositories {
    [NAME]: SubscriberRepository;
  }
}